import React, { useState, useRef, useEffect } from 'react';
import { Button, Card, Col, DatePicker, Form, Input, Row, Table, Typography } from 'antd';
import { SearchOutlined, DownloadOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import moment from 'moment';
import { Excel } from 'antd-table-saveas-excel';
import { CollectionsService } from 'libs/shared-services/finance/src/lib/collections-service';
import { AlertMessages } from '@gtpl/shared-utils/alert-messages';
import { ProductionLogRequest } from 'libs/shared-models/production-management/src/lib/production-inv/production-log.req';

const RodtepAbstractReport = () => {
  const [dataSource, setDataSource] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef(null);
  const CollectionService = new CollectionsService();
  const [report, setReport] = useState([]);
  const [form] = Form.useForm();
  const [disable, setDisable] = useState(false);
  const [selectedEstimatedFromDate, setSelectedEstimatedFromDate] = useState(undefined);
  const [selectedEstimatedToDate, setSelectedEstimatedToDate] = useState(undefined);

  const { Text } = Typography;
  const { RangePicker } = DatePicker;

  const getCurrentFinancialYear = () => {
    const today = new Date();
    const year = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
    const start = new Date(year, 3, 1); // April 1st
    const end = new Date(year + 1, 2, 31); // March 31st of next year
    return { start, end };
  };

  const { start, end } = getCurrentFinancialYear();

  useEffect(() => {
    form.setFieldsValue({ dateRange: [moment(start), moment(end)] });
    setSelectedEstimatedFromDate(start);
    setSelectedEstimatedToDate(end);
    getActiveRodTepReport();
  }, []);

  const getActiveRodTepReport = (onReset = false) => {
    let fromDate = onReset ? undefined : selectedEstimatedFromDate;
    let toDate = onReset ? undefined : selectedEstimatedToDate;

    const req = new ProductionLogRequest();
    req.fromDate = fromDate;
    req.toDate = toDate;

    CollectionService.getRodTepAbstractReport(req).then((res) => {
      if (res.status) {
        const data = mapRodtepData(res.data);
        setDataSource(data);
      } else {
        AlertMessages.getErrorMessage(res.internalMessage);
      }
    }).catch(err => {
      AlertMessages.getErrorMessage(err.message);
    });
  };

  const EstimatedETDDate = (value) => {
    if (value) {
      const fromDate = new Date(value[0].format('yyyy-MM-DD'));
      const toDate = new Date(value[1].format('yyyy-MM-DD'));
      setSelectedEstimatedFromDate(fromDate);
      setSelectedEstimatedToDate(toDate);
    }
  };

  const onReset = () => {
    getActiveRodTepReport()
    form.resetFields();
    setSelectedEstimatedFromDate(undefined);
    setSelectedEstimatedToDate(undefined);
  };

  const mapRodtepData = (queryResult) => {
    const yearRange = `${start.getFullYear()}-${end.getFullYear().toString().slice(-2)}`;
    return [
      // {
      //   key: '1',
      //   particulars: `Total RODTEP Availability`,
      //   bmrIpl: queryResult[0].bmriplamount || 0,
      //   bmrExports: queryResult[0].bmrexportamount || 0,
      //   total: queryResult[0].sum || 0,
      // },
      {
        key: '2',
        particulars: <b>{`Total Availability of RODTEP`}</b>,
        bmrIpl: queryResult[0].bmriplamount || 0,
        bmrExports: queryResult[0].bmrexportamount || 0,
        total: queryResult[0].sum || 0,
      },
      {
        key: '3',
        particulars: 'RODTEP Payment-Received',
        bmrIpl: queryResult[0].PAYMENTRECEIVED_bmripl || 0,
        bmrExports: queryResult[0].PAYMENTRECEIVED_bmrexport || 0,
        total: queryResult[0].PAYMENTRECEIVED || 0,
      },
      {
        key: '4',
        particulars: 'RODTEP Sold Up-To-Date But Payment not Received',
        bmrIpl: queryResult[0].rodtep_sold_sum_bmripl || 0,
        bmrExports: queryResult[0].rodtep_sold_sum_bmrexport || 0,
        total: queryResult[0].rodtep_sold_sum || 0,
      },
      {
        key: '5',
        particulars: 'RODTEP Ready To Sell',
        bmrIpl: queryResult[0].rodtep_ready_to_sell_bmripl || 0,
        bmrExports: queryResult[0].rodtep_ready_to_sell_bmrexport || 0,
        total: queryResult[0].rodtep_ready_to_sell || 0,
      },
      {
        key: '6',
        particulars: 'RODTEP Ready To Scrip Generation',
        bmrIpl: queryResult[0].SCRIPT_GENERATION_bmripl || 0,
        bmrExports: queryResult[0].SCRIPT_GENERATION_bmrexport || 0,
        total: queryResult[0].SCRIPT_GENERATION || 0,
      },
      {
        key: '7',
        particulars: 'BRC Updated But Not Shown In ICEGATE',
        bmrIpl: queryResult[0].BRC_UPDATED_bmripl || 0,
        bmrExports: queryResult[0].BRC_UPDATED_bmrexport || 0,
        total: queryResult[0].BRC_UPDATED || 0,
      },
      {
        key: '8',
        particulars: 'Balance BRC-EEFC Amount Not Updated',
        bmrIpl: queryResult[0].BALANCE_BRC_EEFC_AMOUNT_bmripl || 0,
        bmrExports: queryResult[0].BALANCE_BRC_EEFC_AMOUNT_bmrexport || 0,
        total: queryResult[0].BALANCE_BRC_EEFC_AMOUNT || 0,
      },
      {
        key: '9',
        particulars: 'Payment Received Shown In ICEGATE But BRC Not Updated',
        bmrIpl: queryResult[0].PAYMENT_RECEIVED_SHOWN_IN_ICEGATE_bmripl || 0,
        bmrExports: queryResult[0].PAYMENT_RECEIVED_SHOWN_IN_ICEGATE_bmrexport || 0,
        total: queryResult[0].PAYMENT_RECEIVED_SHOWN_IN_ICEGATE || 0,
      },
      {
        key: '10',
        particulars: 'Payment Not Received But Shown In ICEGATE',
        bmrIpl: queryResult[0].PAYMENTNOTRECEIVED_BUTSHOWIN_ICEGATE_bmripl || 0,
        bmrExports: queryResult[0].PAYMENTNOTRECEIVED_BUTSHOWIN_ICEGATE_bmrexport || 0,
        total: queryResult[0].PAYMENTNOTRECEIVED_BUTSHOWIN_ICEGATE || 0,
      },
      {
        key: '11',
        particulars: 'Payment Not Received And Not Shown In ICEGATE',
        bmrIpl: queryResult[0].PAYMENTNOTRECEIVED_BUT_NOTSHOWIN_ICEGATE_bmripl || 0,
        bmrExports: queryResult[0].PAYMENTNOTRECEIVED_BUT_NOTSHOWIN_ICEGATE_bmrexport || 0,
        total: queryResult[0].PAYMENTNOTRECEIVED_BUT_NOTSHOWIN_ICEGATE || 0,
      },
    ];
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ width: 188, marginBottom: 8, display: "block" }}
        />
        <Button
          type="primary"
          onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
          icon={<SearchOutlined />}
          size="small"
          style={{ width: 90, marginRight: 8 }}
        >
          Search
        </Button>
        <Button
          onClick={() => handleReset(clearFilters)}
          size="small"
          style={{ width: 90 }}
        >
          Reset
        </Button>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : "",
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const columns = [
    {
      title: "Particulars",
      dataIndex: "particulars",
      key: "particulars",
      width: "30%",
      ...getColumnSearchProps("particulars"),
    },
    {
      title: "BMR IPL",
      dataIndex: "bmrIpl",
      key: "bmrIpl",
        render:(val,text)=>{
        return(
          <span>{Number(text.bmrIpl).toLocaleString()}</span>
        )
      }
    },
    {
      title: "BMR Exports",
      dataIndex: "bmrExports",
      key: "bmrExports",
        render:(val,text)=>{
        return(
          <span>{Number(text.bmrExports).toLocaleString()}</span>
        )
      }
    },
    {
      title: "Total",
      dataIndex: "total",
      key: "total",
      render:(val,text)=>{
        return(
          <span>{Number(text.total).toLocaleString()}</span>
        )
      }
    },
  ];

  const exportExcel = () => {
    const excel = new Excel();
    excel
      .addSheet('Rodtep Report')
      .addColumns([
        { title: 'Particulars', dataIndex: 'particulars', },
        { title: 'BMR IPL', dataIndex: 'bmrIpl', },
        { title: 'BMR Exports', dataIndex: 'bmrExports',  },
        { title: 'Total', dataIndex: 'total',  },
      ])
      .addDataSource(dataSource.map(item => ({
        particulars: item.particulars,
        bmrIpl: item.bmrIpl,
        bmrExports: item.bmrExports,
        total: item.total,
      })), { str2num: true })
      .saveAs('rodtep-report.xlsx');
  };

  return (
    <Card
    size="small"
    title={<span style={{ color: 'white' }}>ABSTRACT OF THE RODTEP SCRIPS</span>}
    extra={
      <Button icon={<DownloadOutlined />} onClick={exportExcel} style={{ marginRight: 30 }}>
        GET EXCEL
      </Button>
    }
    style={{ textAlign: 'center' }}
    headStyle={{ backgroundColor: '#69c0ff', border: 0 }}
  >
    {/* <Form form={form} onFinish={search}>
      <Row gutter={24}>
        <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 8 }} xl={{ span: 6 }}>
          <Form.Item label="Date" name="date">
            <RangePicker />
          </Form.Item>
        </Col>
        <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 8 }} xl={{ span: 6 }}>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              Search
            </Button>
          </Form.Item>
        </Col>
      </Row>
    </Form> */}
   <Form layout='vertical' form={form}>
<Row gutter={16} align="middle">
<Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 10 }} lg={{ span: 8 }} xl={{ span: 6 }}>
    <Form.Item
      name="fromDate"
      label="Date Range"
      initialValue={undefined}
      rules={[
        {
          required: false,
          message: "Select date range"
        },
      ]}
    >
      <RangePicker onChange={EstimatedETDDate} />
    </Form.Item>
  </Col>
  <Col style={{ paddingLeft: '10px', marginTop: '7px' }}>
    <Button 
      type="primary" 
      
      disabled={disable} 
      onClick={() => getActiveRodTepReport()}
    >
      Get Report
    </Button>
  </Col>
  <Col style={{ paddingLeft: '10px', marginTop: '7px' }}>
    <Button 
      
      type="primary" 
     
      onClick={onReset}
    > 
      Reset 
    </Button>
  </Col>
</Row>
</Form>
    <Table
      size="small"
      columns={columns}
      dataSource={dataSource}
      bordered
      pagination={false}
      summary={(pageData) => {
        let totalBmrIpl = 0;
        let totalBmrExports = 0;
        let totalSum = 0;
        pageData.forEach(({ bmrIpl, bmrExports, total }, index) => {
          // Calculate totals only for columns 3 to 10
          if (index >= 2 && index <= 9) {
            totalBmrIpl += Number(bmrIpl);
            totalBmrExports += Number(bmrExports);
            totalSum += Number(total);
          }
       
        });

        return (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0}><Text>Total</Text></Table.Summary.Cell>
            <Table.Summary.Cell index={1} align="right"><Text>{Number(totalBmrIpl).toLocaleString()}</Text></Table.Summary.Cell>
            <Table.Summary.Cell index={2} align="right"><Text>{Number(totalBmrExports).toLocaleString()}</Text></Table.Summary.Cell>
            <Table.Summary.Cell index={3} align="right"><Text>{Number(totalSum).toLocaleString()}</Text></Table.Summary.Cell>
          </Table.Summary.Row>
        );
      }}
    />
  </Card>
);
};

export default RodtepAbstractReport;
